import { resolveApiBaseUrl } from "./apiBase";

function createRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  // Fallback: generate an RFC4122-ish UUID so backend UUID validation passes
  // even on browsers without crypto.randomUUID.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

const PREP_SCORE_CACHE_KEY = "the99.prepScore.last";
const PREP_SCORE_TIMEOUT_MS = 15000;

function readCachedPrepScore() {
  try {
    const raw = localStorage.getItem(PREP_SCORE_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedPrepScore(data) {
  try {
    localStorage.setItem(PREP_SCORE_CACHE_KEY, JSON.stringify({ ...data, _cachedAt: Date.now() }));
  } catch {}
}

export async function requestPreparationScore({
  userId,
  sessionId = null,
  context = {},
  tier = "free",
  asyncAllowed = true,
  forceRegenerate = false,
  maxHistorySessions = 12,
  isOffline = false,
}) {
  if (!userId) {
    throw new Error("Missing userId for preparation score request");
  }

  // If offline (forced or detected), return cached score immediately
  if (isOffline || !navigator.onLine) {
    const cached = readCachedPrepScore();
    if (cached) return { ...cached, _fromCache: true };
    throw new Error("Offline — no cached preparation score available");
  }

  const baseUrl = resolveApiBaseUrl();
  const requestId = createRequestId();
  const payload = {
    task: "preparation_score",
    requestId,
    userId,
    sessionId,
    tier,
    asyncAllowed,
    forceRegenerate,
    maxHistorySessions,
    context,
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PREP_SCORE_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${baseUrl}/v1/ai/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (err) {
    const cached = readCachedPrepScore();
    if (cached) return { ...cached, _fromCache: true };
    if (err?.name === "AbortError") {
      throw new Error(`AI API timed out after ${Math.round(PREP_SCORE_TIMEOUT_MS / 1000)}s`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const text = await response.text();
    // Fall back to cache on network error before throwing
    const cached = readCachedPrepScore();
    if (cached) return { ...cached, _fromCache: true };
    throw new Error(text || `AI API request failed (${response.status})`);
  }

  const data = await response.json();
  writeCachedPrepScore(data);
  return data;
}


// ── Pattern Insight ──────────────────────────────────────────────────────
//
// Personal-pattern analysis scoped to a single NOAA tide station. The
// server reads the user's last N sessions+catches at that station from
// Firestore and asks Claude Sonnet 4.5 to surface real patterns. See
// `functions-python/app/tasks/pattern_insight.py` for the strategy.

const PATTERN_INSIGHT_TIMEOUT_MS = 60000;
// Bump the version segment whenever the result shape or server-side derivation
// changes in a way that should invalidate previously cached results. v2 orphans
// pre-2026-06-23 caches that could hold a non-empty sampleSize with an empty
// `hotspots` array (the LLM-drop bug, since fixed server-side) — those caches
// don't self-heal because the self-heal path only triggers on sampleSize === 0.
const PATTERN_INSIGHT_CACHE_PREFIX = "the99.patternInsight.v2.";

function patternInsightCacheKey(userId, stationId) {
  const day = new Date().toISOString().slice(0, 10); // YYYY-MM-DD UTC
  return `${PATTERN_INSIGHT_CACHE_PREFIX}${userId}|${stationId}|${day}`;
}

function readCachedPatternInsight(userId, stationId) {
  if (!userId || !stationId) return null;
  try {
    const raw = localStorage.getItem(patternInsightCacheKey(userId, stationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Empty-station results (sampleSize === 0) self-heal: a user who just
    // logged their first session at this station shouldn't be stuck on a
    // stale "0 sessions" cache for the rest of the day. Drop it and let
    // the network fetch run fresh (and bypass the server cache too — see
    // hadStaleZeroCache below).
    const cachedSampleSize = Number(parsed?.result?.sampleSize ?? 0);
    if (cachedSampleSize === 0) {
      try { localStorage.removeItem(patternInsightCacheKey(userId, stationId)); } catch {}
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function hadStaleZeroCache(userId, stationId) {
  // Returns true if a sampleSize=0 cache existed for today's bucket.
  // Used to flip forceRegenerate on so we also bust the server-side
  // day-bucket cache, not just the local one.
  if (!userId || !stationId) return false;
  try {
    const raw = localStorage.getItem(patternInsightCacheKey(userId, stationId));
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Number(parsed?.result?.sampleSize ?? 0) === 0;
  } catch {
    return false;
  }
}

function writeCachedPatternInsight(userId, stationId, data) {
  if (!userId || !stationId) return;
  // Skip writing empty-station responses — see readCachedPatternInsight.
  const sampleSize = Number(data?.result?.sampleSize ?? 0);
  if (sampleSize === 0) return;
  try {
    localStorage.setItem(
      patternInsightCacheKey(userId, stationId),
      JSON.stringify({ ...data, _cachedAt: Date.now() })
    );
  } catch {}
}

export async function requestPatternInsight({
  userId,
  targetStationId,
  targetStationName = null,
  maxHistorySessions = 100,
  forceRegenerate = false,
  isOffline = false,
}) {
  if (!userId) {
    throw new Error("Missing userId for pattern insight request");
  }
  if (!targetStationId) {
    throw new Error("Missing targetStationId for pattern insight request");
  }

  // Same-day cache check (mirrors server-side day-bucket cache key).
  // If a stale 0-session cache exists, also bust the server cache by
  // forcing regeneration on this call.
  const staleZero = hadStaleZeroCache(userId, targetStationId);
  if (!forceRegenerate) {
    const cached = readCachedPatternInsight(userId, targetStationId);
    if (cached) return { ...cached, _fromCache: true };
  }
  const effectiveForceRegenerate = forceRegenerate || staleZero;

  if (isOffline || (typeof navigator !== "undefined" && navigator.onLine === false)) {
    const cached = readCachedPatternInsight(userId, targetStationId);
    if (cached) return { ...cached, _fromCache: true };
    throw new Error("Offline — no cached pattern insight available");
  }

  const baseUrl = resolveApiBaseUrl();
  const payload = {
    task: "pattern_insight",
    requestId: createRequestId(),
    userId,
    sessionId: null,
    tier: "free",
    asyncAllowed: false,
    forceRegenerate: effectiveForceRegenerate,
    maxHistorySessions,
    context: {
      targetStationId: String(targetStationId),
      targetStationName: targetStationName || null,
    },
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PATTERN_INSIGHT_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${baseUrl}/v1/ai/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (err) {
    const cached = readCachedPatternInsight(userId, targetStationId);
    if (cached) return { ...cached, _fromCache: true };
    if (err?.name === "AbortError") {
      throw new Error(
        `Pattern Insight timed out after ${Math.round(PATTERN_INSIGHT_TIMEOUT_MS / 1000)}s`
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const text = await response.text();
    const cached = readCachedPatternInsight(userId, targetStationId);
    if (cached) return { ...cached, _fromCache: true };
    throw new Error(text || `Pattern Insight request failed (${response.status})`);
  }

  const data = await response.json();
  if (data?.status === "ready") {
    writeCachedPatternInsight(userId, targetStationId, data);
  }
  return data;
}


// ── Session Analysis ───────────────────────────────────────────────────────
//
// LLM-generated recap of a SINGLE completed session, shown on the Trip
// Report. Pro-only (the server re-checks entitlement and returns a
// PRO_REQUIRED error for free users). The server reads the session + its
// catches from Firestore, pre-computes a facts table, and asks Claude
// Sonnet 4.5 to narrate it. See
// `functions-python/app/tasks/session_analysis.py`.

const SESSION_ANALYSIS_TIMEOUT_MS = 30000;
const SESSION_ANALYSIS_CACHE_PREFIX = "the99.sessionAnalysis.";

function sessionAnalysisCacheKey(userId, sessionId) {
  return `${SESSION_ANALYSIS_CACHE_PREFIX}${userId}|${sessionId}`;
}

function readCachedSessionAnalysis(userId, sessionId) {
  if (!userId || !sessionId) return null;
  try {
    const raw = localStorage.getItem(sessionAnalysisCacheKey(userId, sessionId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedSessionAnalysis(userId, sessionId, data) {
  if (!userId || !sessionId) return;
  try {
    localStorage.setItem(
      sessionAnalysisCacheKey(userId, sessionId),
      JSON.stringify({ ...data, _cachedAt: Date.now() })
    );
  } catch {}
}

/**
 * Thrown when the server rejects the request because the user is not Pro.
 * Lets the UI render an upsell state instead of a generic error.
 */
export class ProRequiredError extends Error {
  constructor(message = "AI Session Analysis is a Pro feature.") {
    super(message);
    this.name = "ProRequiredError";
    this.code = "PRO_REQUIRED";
  }
}

export async function requestSessionAnalysis({
  userId,
  sessionId,
  stationName = null,
  forceRegenerate = false,
  isOffline = false,
}) {
  if (!userId) {
    throw new Error("Missing userId for session analysis request");
  }
  if (!sessionId) {
    throw new Error("Missing sessionId for session analysis request");
  }

  // A session's data is stable once it has ended, so a cached recap is
  // safe to reuse and avoids re-spending tokens on every report view.
  if (!forceRegenerate) {
    const cached = readCachedSessionAnalysis(userId, sessionId);
    if (cached) return { ...cached, _fromCache: true };
  }

  if (isOffline || (typeof navigator !== "undefined" && navigator.onLine === false)) {
    const cached = readCachedSessionAnalysis(userId, sessionId);
    if (cached) return { ...cached, _fromCache: true };
    throw new Error("Offline — no cached session analysis available");
  }

  const baseUrl = resolveApiBaseUrl();
  const payload = {
    task: "session_analysis",
    requestId: createRequestId(),
    userId,
    sessionId: String(sessionId),
    tier: "pro",
    asyncAllowed: false,
    forceRegenerate,
    maxHistorySessions: 0,
    context: {
      stationName: stationName || null,
    },
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SESSION_ANALYSIS_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${baseUrl}/v1/ai/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (err) {
    const cached = readCachedSessionAnalysis(userId, sessionId);
    if (cached) return { ...cached, _fromCache: true };
    if (err?.name === "AbortError") {
      throw new Error(
        `Session Analysis timed out after ${Math.round(SESSION_ANALYSIS_TIMEOUT_MS / 1000)}s`
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let parsed = null;
    const text = await response.text();
    try {
      parsed = JSON.parse(text);
    } catch {}
    if (parsed?.error?.code === "PRO_REQUIRED") {
      throw new ProRequiredError(parsed.error.message);
    }
    const cached = readCachedSessionAnalysis(userId, sessionId);
    if (cached) return { ...cached, _fromCache: true };
    throw new Error(text || `Session Analysis request failed (${response.status})`);
  }

  const data = await response.json();
  if (data?.status === "error" && data?.error?.code === "PRO_REQUIRED") {
    throw new ProRequiredError(data.error.message);
  }
  if (data?.status === "ready") {
    writeCachedSessionAnalysis(userId, sessionId, data);
  }
  return data;
}

// ── Tournament Scout ───────────────────────────────────────────────────────
//
// LLM-generated pre-tournament scouting brief, built from the angler's own
// prefishing sessions (prefishFor == tournamentId) + their historical
// sessions at the tournament water. Pro-only (server re-checks entitlement).
// See `functions-python/app/tasks/tournament_scout.py`.

const TOURNAMENT_SCOUT_TIMEOUT_MS = 30000;
const TOURNAMENT_SCOUT_CACHE_PREFIX = "the99.tournamentScout.";

function tournamentScoutCacheKey(userId, tournamentId) {
  // Day-bucketed: prefish data + forecast drift daily, so a same-day re-open
  // reuses the brief but tomorrow re-generates.
  const day = new Date().toISOString().slice(0, 10);
  return `${TOURNAMENT_SCOUT_CACHE_PREFIX}${userId}|${tournamentId}|${day}`;
}

function readCachedTournamentScout(userId, tournamentId) {
  if (!userId || !tournamentId) return null;
  try {
    const raw = localStorage.getItem(tournamentScoutCacheKey(userId, tournamentId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedTournamentScout(userId, tournamentId, data) {
  if (!userId || !tournamentId) return;
  try {
    localStorage.setItem(
      tournamentScoutCacheKey(userId, tournamentId),
      JSON.stringify({ ...data, _cachedAt: Date.now() })
    );
  } catch {}
}

export async function requestTournamentScout({
  userId,
  tournamentId,
  tournamentName = null,
  targetStationId = null,
  targetStationName = null,
  allowedSpecies = [],
  forecast = null,
  forceRegenerate = false,
  isOffline = false,
}) {
  if (!userId) throw new Error("Missing userId for tournament scout request");
  if (!tournamentId) throw new Error("Missing tournamentId for tournament scout request");

  if (!forceRegenerate) {
    const cached = readCachedTournamentScout(userId, tournamentId);
    if (cached) return { ...cached, _fromCache: true };
  }

  if (isOffline || (typeof navigator !== "undefined" && navigator.onLine === false)) {
    const cached = readCachedTournamentScout(userId, tournamentId);
    if (cached) return { ...cached, _fromCache: true };
    throw new Error("Offline — no cached tournament scout available");
  }

  const baseUrl = resolveApiBaseUrl();
  const payload = {
    task: "tournament_scout",
    requestId: createRequestId(),
    userId,
    tier: "pro",
    asyncAllowed: false,
    forceRegenerate,
    maxHistorySessions: 100,
    context: {
      tournamentId: String(tournamentId),
      tournamentName: tournamentName || null,
      targetStationId: targetStationId || null,
      targetStationName: targetStationName || null,
      allowedSpecies: Array.isArray(allowedSpecies) ? allowedSpecies : [],
      forecast: forecast || null,
    },
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TOURNAMENT_SCOUT_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(`${baseUrl}/v1/ai/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (err) {
    const cached = readCachedTournamentScout(userId, tournamentId);
    if (cached) return { ...cached, _fromCache: true };
    if (err?.name === "AbortError") {
      throw new Error(
        `Tournament Scout timed out after ${Math.round(TOURNAMENT_SCOUT_TIMEOUT_MS / 1000)}s`
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let parsed = null;
    const text = await response.text();
    try {
      parsed = JSON.parse(text);
    } catch {}
    if (parsed?.error?.code === "PRO_REQUIRED") {
      throw new ProRequiredError(parsed.error.message);
    }
    const cached = readCachedTournamentScout(userId, tournamentId);
    if (cached) return { ...cached, _fromCache: true };
    throw new Error(text || `Tournament Scout request failed (${response.status})`);
  }

  const data = await response.json();
  if (data?.status === "error" && data?.error?.code === "PRO_REQUIRED") {
    throw new ProRequiredError(data.error.message);
  }
  if (data?.status === "ready") {
    writeCachedTournamentScout(userId, tournamentId, data);
  }
  return data;
}
